"use client"

import React, { useEffect, useState } from "react"
import { useRouter } from "next/router"

import { Button, Sheet, SheetContent, SheetTitle } from "@coval/ui"
import { Menu } from "lucide-react"

import { SidebarNavPanel } from "./SidebarNavPanel"

/**
 * Clerk mounts the organization switcher's popover in its own portal on the
 * body rather than inside the sheet. The sheet's dismissable layer only knows
 * about its own nodes, so a press in that popover reads as an outside click
 * and would tear the sheet down — unmounting the switcher — before the
 * selection lands. `globals.css` restores hit-testing for the same portal,
 * which a modal Radix layer turns off body-wide.
 */
const CLERK_PORTAL_SELECTOR =
  "[data-floating-ui-portal], .cl-organizationSwitcherPopoverCard"

const isInsideClerkPortal = (target: EventTarget | null) =>
  target instanceof Element && target.closest(CLERK_PORTAL_SELECTOR) !== null

interface SidebarNavMobileMenuProps {
  /** Placement classes from the header slot the button sits in. */
  className?: string
}

/**
 * The primary navigation on a viewport too narrow to dock it: a menu button in
 * the page header that opens the whole panel as an overlay. Nothing is dropped
 * at this width — the overlay carries the same sections, rows, organization
 * switcher, and account menu the docked panel does.
 */
export const SidebarNavMobileMenu = ({
  className,
}: SidebarNavMobileMenuProps) => {
  const router = useRouter()
  const [isOpen, setIsOpen] = useState(false)

  // Rows the shell does not own — a module's contributed row, a Sofia thread —
  // navigate without telling the menu, so the route itself closes the overlay.
  useEffect(() => {
    setIsOpen(false)
  }, [router.asPath])

  return (
    <>
      <Button
        variant="ghost"
        icon
        tooltip="Open navigation menu"
        aria-expanded={isOpen}
        data-testid="sidebar-menu-button"
        className={className}
        onClick={() => setIsOpen(true)}
      >
        <Menu />
      </Button>

      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent
          side="left"
          className="w-[260px]"
          aria-describedby={undefined}
          onInteractOutside={(event) => {
            if (isInsideClerkPortal(event.target)) {
              event.preventDefault()
            }
          }}
        >
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SidebarNavPanel
            className="w-full"
            onNavigate={() => setIsOpen(false)}
          />
        </SheetContent>
      </Sheet>
    </>
  )
}
